import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from 'react'
import { crmApi } from '@/api'
import type { CrmLeadCardInFunnelData } from '@/api/types/crm/crmFunnels.types'
import { isError } from '@/helpers/checkQuery'
import { getShoppingCurrentLeadFunnelId } from './leadFunnelId.utils'
import {
  buildLeadAfterModalRefetch,
  fetchLeadCardInFunnelAndPutSnapshot,
  mergeLeadProfileFromGetLeadOnly,
  parseFullLeadFromGetLeadResponse,
  resolveFunnelContextFromLeadCard,
  toValidFunnelId,
  tryParseLeadFromGetLeadResponse,
  type LeadCardApiData,
  type LeadCardCurrentFunnel,
} from './leadModalRefetchMerge'
import type { LeadProfileLead } from './LeadProfileModal.types'
import debounce from '@/utils/debounce/debounce'
import websocket from '@/utils/websocket'
import {
  CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS,
  isIntegrationMessageInsertedWsMessage,
  isIntegrationMessageReadWsMessage,
  isVkMessageInsertedWsMessage,
  isVkMessageReadWsMessage,
  normalizeVkUnreadCount,
  useRefetchOnBrowserTabVisible,
} from '../../utils/crmRealtimeWebSocket'
import { extractVkChatMetaFromPayload, withLeadVkChatFromPayload } from '../../utils/leadVkChatMeta'
import {
  patchLeadProviderChatMeta,
  patchLeadProviderChatUnreadZero,
  patchLeadVkChatMeta,
} from '../../utils/leadFields'
import { parseCrmChatProviderKey } from '../integrations/common/lib/crmChatProviderKey'

function deriveSelectedFunnelIdFromLead(
  lead: LeadProfileLead | undefined,
): number | null {
  if (!lead) return null

  const currentFunnelId = toValidFunnelId(lead.currentFunnelId)
  if (currentFunnelId !== null) return currentFunnelId

  const rawFunnels = Array.isArray(lead.funnels)
    ? (lead.funnels as Array<{
        id?: number | string
        isPrimary?: boolean
        is_primary?: boolean
      }>)
    : []
  const primaryFunnel = rawFunnels.find(
    (funnel) => funnel?.isPrimary || funnel?.is_primary,
  )
  return toValidFunnelId(primaryFunnel?.id)
}

const parsePositiveId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

const extractVkMetaFromGetLeadResponse = (
  response: { data?: { data?: unknown; status?: string } } | undefined,
): unknown => {
  const payload = response?.data?.data
  return extractVkChatMetaFromPayload(payload)
}

const getCardRowForFunnel = (
  card: LeadCardApiData | null,
  funnelId: number,
): LeadCardCurrentFunnel | undefined =>
  card?.current_funnels?.find((cf) => Number(cf.funnel_id) === funnelId)

const getCurrentStatusIdFromCardFunnel = (
  data: CrmLeadCardInFunnelData | undefined,
): number | null => {
  if (!data) return null
  const cur = data.current_status ?? data.status
  return parsePositiveId(cur?.id)
}

/** Один раз вытащить из снимков поля по выбранной воронке: сначала `card-funnel`, иначе строка из `card`. */
function deriveCommittedFunnelSlice(
  selectedFunnelId: number | null,
  cardFunnelSnapshots: Partial<Record<number, CrmLeadCardInFunnelData>>,
  leadCardSnapshot: LeadCardApiData | null,
): {
  committedManagerId: number | null
  committedNextContactAt: string | null
  committedCurrentStatusId: number | null
} {
  const empty = {
    committedManagerId: null as number | null,
    committedNextContactAt: null as string | null,
    committedCurrentStatusId: null as number | null,
  }
  if (
    selectedFunnelId === null ||
    !Number.isFinite(Number(selectedFunnelId))
  ) {
    return empty
  }
  const fid = Number(selectedFunnelId)
  const deep = cardFunnelSnapshots[fid]
  const cardRow =
    deep === undefined ? getCardRowForFunnel(leadCardSnapshot, fid) : undefined

  const committedManagerId =
    deep !== undefined
      ? parsePositiveId(deep.manager?.id)
      : parsePositiveId(cardRow?.manager_id)

  let committedNextContactAt: string | null = null
  if (deep !== undefined) {
    const raw = deep.next_contact_at
    if (raw != null && String(raw).trim() !== '') committedNextContactAt = String(raw)
  } else {
    const raw = cardRow?.next_contact_at
    if (raw != null && String(raw).trim() !== '') committedNextContactAt = String(raw)
  }

  const committedCurrentStatusId =
    deep !== undefined
      ? getCurrentStatusIdFromCardFunnel(deep)
      : parsePositiveId(cardRow?.current_status_id)

  return {
    committedManagerId,
    committedNextContactAt,
    committedCurrentStatusId,
  }
}

export type LeadRefetchInModalOptions = {
  /** Без blur/Preloader на модалке (например refetch при возврате на вкладку браузера). */
  silent?: boolean
}

export interface LeadRefetchContextType {
  handleRefetchLeadInModal: (
    funnelId?: number | null,
    options?: LeadRefetchInModalOptions,
  ) => Promise<LeadProfileLead | undefined>
  /** После закрытия чата ВК из карточки лида — get-lead + card (как при сохранении). */
  refetchLeadModalAfterVkChatClose: () => Promise<void>
  /**
   * Два подряд рефетча с той же воронкой, что при открытии модалки (getLead + card + card-funnel).
   * Нужно после покупки/создания ЛК: бэк иногда отдаёт старый user_id на первом ответе.
   */
  refetchLeadHardAfterMutation: () => Promise<void>
  /** Локально подмешать поля лида (например user_id сразу после привязки ЛК). */
  patchLead: (partial: Partial<LeadProfileLead>) => void
  /** Текущий лид карточки (для UI-состояния полей и т.п.). */
  lead: LeadProfileLead | undefined
  handleRefetchLeadsOnClose: () => void
  resolveLeadFunnelId: (params?: {
    lead?: LeadProfileLead | null
    selectedFunnelId?: number | null
  }) => number | undefined
  isRefetchingLeadCard: boolean
  selectedFunnelId: number | null
  selectFunnel: (funnelId: number | null) => void
  selectedFunnelIdRef: MutableRefObject<number | null>
  leadCardSnapshot: LeadCardApiData | null
  committedManagerId: number | null
  committedNextContactAt: string | null
  committedCurrentStatusId: number | null
  getCardInFunnelSnapshot: (funnelId: number) => CrmLeadCardInFunnelData | undefined
  putCardInFunnelSnapshot: (
    funnelId: number,
    data: CrmLeadCardInFunnelData,
  ) => void
  /** Увеличивается при каждом `putCardInFunnelSnapshot` — для синхронизации UI без лишнего запроса card-funnel. */
  cardFunnelSnapshotEpoch: number
  initialFunnelIdForRefetch: number | null
  setInitialFunnelIdForRefetch: Dispatch<SetStateAction<number | null>>
  state: {
    needRefetchLeads: boolean
    setNeedRefetchLeads: (value: boolean) => void
  }
}

const LeadRefetchContext = createContext<LeadRefetchContextType | undefined>(
  undefined,
)

interface LeadRefetchProviderProps {
  children: ReactNode
  leadId?: number
  setLead: Dispatch<SetStateAction<LeadProfileLead>>
  lead: LeadProfileLead | undefined
  open: boolean
  initialFunnelIdForRefetch: number | null
  setInitialFunnelIdForRefetch: Dispatch<SetStateAction<number | null>>
}

export const LeadRefetchProvider: React.FC<LeadRefetchProviderProps> = ({
  children,
  leadId,
  setLead,
  lead,
  open,
  initialFunnelIdForRefetch,
  setInitialFunnelIdForRefetch,
}) => {
  const [needRefetchLeads, setNeedRefetchLeads] = useState(false)
  const [isRefetchingLeadCard, setIsRefetchingLeadCard] = useState(false)
  const [selectedFunnelId, setSelectedFunnelId] = useState<number | null>(() =>
    deriveSelectedFunnelIdFromLead(lead),
  )
  const selectedFunnelIdRef = useRef<number | null>(
    deriveSelectedFunnelIdFromLead(lead),
  )

  const [leadCardSnapshot, setLeadCardSnapshot] = useState<LeadCardApiData | null>(
    null,
  )
  const [cardFunnelSnapshots, setCardFunnelSnapshots] = useState<
    Partial<Record<number, CrmLeadCardInFunnelData>>
  >({})
  const [cardFunnelSnapshotEpoch, setCardFunnelSnapshotEpoch] = useState(0)

  const putCardInFunnelSnapshot = useCallback(
    (funnelId: number, data: CrmLeadCardInFunnelData) => {
      if (!Number.isFinite(funnelId) || funnelId <= 0) return
      setCardFunnelSnapshots((prev) => ({ ...prev, [funnelId]: data }))
      setCardFunnelSnapshotEpoch((e) => e + 1)
    },
    [],
  )

  const getCardInFunnelSnapshot = useCallback(
    (funnelId: number) => {
      if (!Number.isFinite(funnelId) || funnelId <= 0) return undefined
      return cardFunnelSnapshots[funnelId]
    },
    [cardFunnelSnapshots],
  )

  useEffect(() => {
    if (!leadId) setNeedRefetchLeads(false)
  }, [leadId])

  useEffect(() => {
    setLeadCardSnapshot(null)
    setCardFunnelSnapshots({})
    setCardFunnelSnapshotEpoch(0)
  }, [leadId])

  useEffect(() => {
    if (!lead) {
      setSelectedFunnelId(null)
      selectedFunnelIdRef.current = null
      return
    }

    const validFunnelIds = new Set<number>()
    for (const f of lead.funnels ?? []) {
      const id = Number(f.id)
      if (Number.isFinite(id) && id > 0) validFunnelIds.add(id)
    }
    if (lead.funnel?.id) {
      const id = Number(lead.funnel.id)
      if (Number.isFinite(id) && id > 0) validFunnelIds.add(id)
    }

    const fromLead = deriveSelectedFunnelIdFromLead(lead)

    setSelectedFunnelId((prev) => {
      const next =
        prev !== null && validFunnelIds.has(prev) ? prev : fromLead
      selectedFunnelIdRef.current = next
      return next
    })
  }, [lead])

  const resolvedInitialFunnelId = useMemo(() => {
    const fromProp = toValidFunnelId(initialFunnelIdForRefetch)
    if (fromProp !== null) return fromProp

    const fromLeadCurrent = toValidFunnelId(lead?.currentFunnelId)
    if (fromLeadCurrent !== null) return fromLeadCurrent

    return toValidFunnelId(lead?.funnels?.[0]?.id)
  }, [initialFunnelIdForRefetch, lead?.currentFunnelId, lead?.funnels])

  const handleRefetchLeadInModal = useCallback(
    async (
      funnelId?: number | null,
      options?: LeadRefetchInModalOptions,
    ): Promise<LeadProfileLead | undefined> => {
      const numericLeadId = Number(leadId)
      if (!Number.isFinite(numericLeadId) || numericLeadId <= 0) {
        return undefined
      }
      const silent = options?.silent === true
      if (!silent) {
        setIsRefetchingLeadCard(true)
      }
      let refetchedLead: LeadProfileLead | undefined
      try {
        const requestedFunnelId = toValidFunnelId(funnelId)

        const [cardResponse, leadDetailsResponse] = await Promise.all([
          crmApi.leads.getLeadCard({ lead_id: numericLeadId }),
          crmApi.getLead({ lead_id: numericLeadId }),
        ])

        const cardData = cardResponse?.data?.data as LeadCardApiData | undefined
        const fullLeadFromDetails =
          parseFullLeadFromGetLeadResponse(leadDetailsResponse)

        if (!cardData) {
          if (fullLeadFromDetails) {
            setLead((prev) => {
              if (!prev) {
                return prev
              }
              refetchedLead = mergeLeadProfileFromGetLeadOnly(
                prev,
                fullLeadFromDetails,
              )
              return refetchedLead
            })
          }
          return refetchedLead
        }

        setLeadCardSnapshot(cardData)

        const { currentFunnels, resolvedFunnelId, rowForResolved } =
          resolveFunnelContextFromLeadCard(cardData, requestedFunnelId)

        let managerIdFromCardFunnel: number | null | undefined = undefined
        if (resolvedFunnelId !== null) {
          managerIdFromCardFunnel = await fetchLeadCardInFunnelAndPutSnapshot({
            numericLeadId,
            resolvedFunnelId,
            putCardInFunnelSnapshot,
          })
        }

        setLead((prev) => {
          if (!prev) {
            return prev
          }
          refetchedLead = buildLeadAfterModalRefetch({
            prev,
            fullLeadFromDetails,
            cardData,
            resolvedFunnelId,
            rowForResolved,
            managerIdFromCardFunnel,
            currentFunnels,
          })
          return refetchedLead
        })
        return refetchedLead
      } catch (error) {
        console.error('Failed to refetch lead:', error)
        return undefined
      } finally {
        if (!silent) {
          setIsRefetchingLeadCard(false)
        }
      }
    },
    [leadId, setLead, putCardInFunnelSnapshot],
  )

  const handleRefetchRef = useRef(handleRefetchLeadInModal)
  handleRefetchRef.current = handleRefetchLeadInModal

  const refetchLeadModalAfterVkChatClose = useCallback(async () => {
    const numericLeadId = Number(leadId)
    if (!Number.isFinite(numericLeadId) || numericLeadId <= 0) {
      return
    }

    try {
      const leadDetailsResponse = await crmApi.getLead({
        lead_id: numericLeadId,
      })
      const fullLeadFromDetails =
        parseFullLeadFromGetLeadResponse(leadDetailsResponse)
      const vkMetaFromResponse =
        extractVkMetaFromGetLeadResponse(leadDetailsResponse)

      setLead((prev) => {
        if (!prev) {
          return prev
        }
        const nextLead =
          fullLeadFromDetails != null
            ? mergeLeadProfileFromGetLeadOnly(prev, fullLeadFromDetails)
            : prev

        const payload = leadDetailsResponse?.data?.data
        if (payload == null) {
          if (vkMetaFromResponse === undefined) {
            return nextLead
          }
          return patchLeadVkChatMeta(
            nextLead,
            vkMetaFromResponse as any,
          ) as LeadProfileLead
        }

        return withLeadVkChatFromPayload(nextLead, payload) as LeadProfileLead
      })
    } catch (error) {
      console.error('Failed to refetch lead after VK chat close:', error)
    }
  }, [leadId, setLead])

  const refetchLeadHardAfterMutation = useCallback(async () => {
    const fid = resolvedInitialFunnelId
    await handleRefetchLeadInModal(fid)
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 500)
    })
    await handleRefetchLeadInModal(fid)
  }, [handleRefetchLeadInModal, resolvedInitialFunnelId])

  const bootstrapRefetchKeyRef = useRef<string>('')

  useEffect(() => {
    if (!open) {
      bootstrapRefetchKeyRef.current = ''
      return
    }
    if (leadId == null || !Number.isFinite(Number(leadId)) || Number(leadId) <= 0) {
      return
    }
    // Ждём seed/stub: иначе первый проход с funnel=null, второй с id = двойной blur
    if (!lead) return

    const fid = resolvedInitialFunnelId
    if (fid === null) {
      const hasFunnelCandidate =
        toValidFunnelId(lead.currentFunnelId) != null ||
        toValidFunnelId(
          (lead as { funnel?: { id?: number | null } | null }).funnel?.id,
        ) != null ||
        (Array.isArray(lead.funnels) &&
          lead.funnels.some((f) => toValidFunnelId(f.id) != null))
      // Воронка ещё должна резолвнуться — не стартуем пустой bootstrap
      if (hasFunnelCandidate) return
    }

    const key = `${leadId}:${fid ?? 'null'}`
    if (bootstrapRefetchKeyRef.current === key) return
    bootstrapRefetchKeyRef.current = key
    void handleRefetchRef.current(fid)
  }, [open, resolvedInitialFunnelId, leadId, lead])

  const refetchLeadChatMetaSilent = useCallback(async () => {
    const numericLeadId = Number(leadId)
    if (!Number.isFinite(numericLeadId) || numericLeadId <= 0) return

    try {
      const response = await crmApi.getLead({ lead_id: numericLeadId })
      if (isError(response)) return

      // Как в leadModalRefetchMerge: integrations могут быть на data или data.lead
      const parsedLead = tryParseLeadFromGetLeadResponse(response)
      const payload = response?.data?.data
      if (parsedLead == null && payload == null) return

      setLead((prev) => {
        if (!prev) return prev
        if (payload != null) {
          return withLeadVkChatFromPayload(prev, payload) as LeadProfileLead
        }
        return withLeadVkChatFromPayload(prev, parsedLead) as LeadProfileLead
      })
    } catch {
      // тихий catch-up счётчиков интеграций — без алертов
    }
  }, [leadId, setLead])

  const refetchLeadChatMetaSilentRef = useRef(refetchLeadChatMetaSilent)
  refetchLeadChatMetaSilentRef.current = refetchLeadChatMetaSilent

  const debouncedVisibleTabVkRefetchRef = useRef(
    debounce(() => {
      void refetchLeadChatMetaSilentRef.current()
    }, CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS),
  )

  /** При возврате на вкладку — integrations.* / vk unread, без перезаписи fields. */
  useRefetchOnBrowserTabVisible(() => {
    debouncedVisibleTabVkRefetchRef.current()
  }, open && leadId != null && Number.isFinite(Number(leadId)) && Number(leadId) > 0)

  const debouncedVkWsLeadRefetchRef = useRef(
    debounce(() => {
      void refetchLeadChatMetaSilentRef.current()
    }, CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS),
  )

  /** WS: сразу патчим unread из payload + тихий get-lead для полной меты. */
  useEffect(() => {
    if (
      !open ||
      leadId == null ||
      !Number.isFinite(Number(leadId)) ||
      Number(leadId) <= 0
    ) {
      return
    }

    const numericLeadId = Number(leadId)

    websocket.connect()

    const applyUnreadFromWs = (
      providerRaw: string | null | undefined,
      unreadRaw: unknown,
    ) => {
      const provider = parseCrmChatProviderKey(providerRaw) ?? 'vkontakte'
      const unreadCount = normalizeVkUnreadCount(unreadRaw)

      setLead((prev) => {
        if (!prev) {
          return prev
        }

        if (unreadCount <= 0) {
          return patchLeadProviderChatUnreadZero(
            prev,
            provider,
          ) as LeadProfileLead
        }

        return patchLeadProviderChatMeta(prev, provider, {
          unread_count: unreadCount,
        }) as LeadProfileLead
      })
    }

    const onVkWs = (message: unknown) => {
      if (isIntegrationMessageInsertedWsMessage(message)) {
        const payload = message.data.payload
        if (Number(payload.lead_id) !== numericLeadId) {
          return
        }
        applyUnreadFromWs(payload.provider, payload.unread_count)
        debouncedVkWsLeadRefetchRef.current()
        return
      }

      // unread_count в read = 0 только для conversation, не сумма по лиду/провайдеру.
      if (isIntegrationMessageReadWsMessage(message)) {
        if (Number(message.data.payload.lead_id) !== numericLeadId) {
          return
        }
        debouncedVkWsLeadRefetchRef.current()
        return
      }

      if (
        !isVkMessageInsertedWsMessage(message) &&
        !isVkMessageReadWsMessage(message)
      ) {
        return
      }

      if (Number(message.data.payload.lead_id) !== numericLeadId) {
        return
      }

      applyUnreadFromWs('vkontakte', message.data.payload.unread_count)
      debouncedVkWsLeadRefetchRef.current()
    }

    const unsubscribeInserted = websocket.subscribe(
      'vk_message_inserted',
      onVkWs,
    )
    const unsubscribeRead = websocket.subscribe('vk_message_read', onVkWs)
    const unsubscribeIntegration = websocket.subscribe(
      'integration_message_inserted',
      onVkWs,
    )
    const unsubscribeIntegrationRead = websocket.subscribe(
      'integration_message_read',
      onVkWs,
    )

    return () => {
      unsubscribeInserted?.()
      unsubscribeRead?.()
      unsubscribeIntegration?.()
      unsubscribeIntegrationRead?.()
    }
  }, [leadId, open, setLead])

  /** UI выбрал воронку → обновляем id и тянем `card` + `card-funnel`, чтобы снимки совпали с сервером. */
  const selectFunnel = useCallback(
    (funnelId: number | null) => {
      selectedFunnelIdRef.current = funnelId
      setSelectedFunnelId(funnelId)
      if (
        leadId &&
        funnelId !== null &&
        Number.isFinite(Number(funnelId)) &&
        Number(funnelId) > 0
      ) {
        void handleRefetchRef.current?.(funnelId)
      }
    },
    [leadId],
  )

  const patchLead = useCallback((partial: Partial<LeadProfileLead>) => {
    setLead((prev) =>
      prev ? ({ ...prev, ...partial } as LeadProfileLead) : prev,
    )
  }, [setLead])

  const {
    committedManagerId,
    committedNextContactAt,
    committedCurrentStatusId,
  } = useMemo(
    () =>
      deriveCommittedFunnelSlice(
        selectedFunnelId,
        cardFunnelSnapshots,
        leadCardSnapshot,
      ),
    [selectedFunnelId, cardFunnelSnapshots, leadCardSnapshot],
  )

  const resolveLeadFunnelId = useCallback(
    (params?: {
      lead?: LeadProfileLead | null
      selectedFunnelId?: number | null
    }) => {
      const L = params?.lead !== undefined ? params.lead : lead
      const S =
        params?.selectedFunnelId !== undefined
          ? params.selectedFunnelId
          : selectedFunnelId
      if (!L) return undefined
      return getShoppingCurrentLeadFunnelId({
        lead: L,
        selectedFunnelId: S ?? null,
      })
    },
    [lead, selectedFunnelId],
  )

  const handleRefetchLeadsOnClose = useCallback(() => {
    setNeedRefetchLeads(true)
  }, [])

  const value: LeadRefetchContextType = {
    handleRefetchLeadInModal,
    refetchLeadModalAfterVkChatClose,
    refetchLeadHardAfterMutation,
    patchLead,
    lead,
    handleRefetchLeadsOnClose,
    resolveLeadFunnelId,
    isRefetchingLeadCard,
    selectedFunnelId,
    selectFunnel,
    selectedFunnelIdRef,
    leadCardSnapshot,
    committedManagerId,
    committedNextContactAt,
    committedCurrentStatusId,
    getCardInFunnelSnapshot,
    putCardInFunnelSnapshot,
    cardFunnelSnapshotEpoch,
    initialFunnelIdForRefetch,
    setInitialFunnelIdForRefetch,
    state: {
      needRefetchLeads,
      setNeedRefetchLeads,
    },
  }

  return (
    <LeadRefetchContext.Provider value={value}>
      {children}
    </LeadRefetchContext.Provider>
  )
}

export const useLeadRefetch = (): LeadRefetchContextType => {
  const context = useContext(LeadRefetchContext)
  if (!context) {
    throw new Error('useLeadRefetch must be used within a LeadRefetchProvider')
  }
  return context
}

export const useOptionalLeadRefetch = (): LeadRefetchContextType | undefined =>
  useContext(LeadRefetchContext)
